import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, FileDown, RotateCcw, Filter, Trash2 } from "lucide-react";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { BoxSignaturePad } from "@/components/admin/BoxSignaturePad";
import {
  createBoxMovement, downloadBoxReceipt, downloadBoxReport, fetchBoxCatalog, fetchBoxMovement,
  fetchBoxMovements, fetchBoxPeople, registerBoxPayment, registerBoxReturn, reverseBoxMovement,
  reverseBoxSettlement, type BoxCatalogMaterial, type BoxMovement, type BoxMovementFilters,
  type BoxMovementItem, type BoxOperation, type BoxPaymentMethod, type BoxPerson,
  type CreateBoxMovementItemInput,
} from "@/lib/boxControlApi";
import {
  OPERATION_LABEL, PAYMENT_METHODS, PAYMENT_METHOD_LABEL, STATUS_LABEL,
  formatDate, formatDateTimeBrasilia, formatMoney, materialTypeLabel, todayInBrasilia,
} from "@/lib/boxLabels";

function newRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const ALL = "__all__";

function itemsSummary(items: BoxMovementItem[]): string {
  return items.map((it) => `${materialTypeLabel(it.materialName, it.typeName)} ×${it.quantity}`).join(", ");
}

function loanPending(m: BoxMovement): number {
  return m.items.reduce((acc, it) => acc + (it.pendingQuantity ?? 0), 0);
}

export default function AdminBoxControl() {
  const account = useApp((state) => state.account);
  const isAdmin = account?.role === "ADMIN";
  const canLaunch = hasPermission(account, "CAIXAS.MOVIMENTACAO.LANCAR");
  const canReturn = hasPermission(account, "CAIXAS.MOVIMENTACAO.DEVOLVER");
  // Recebimento de pagamento e correção/estorno são EXCLUSIVOS do ADMIN nesta entrega (espelha o backend).
  const canReceive = isAdmin;
  const canCorrect = isAdmin;
  const canCreatePerson = hasPermission(account, "CAIXAS.PESSOA.GERENCIAR");
  const canExport = hasPermission(account, "CAIXAS.RELATORIO.EXPORTAR");

  const [movements, setMovements] = useState<BoxMovement[]>([]);
  const [people, setPeople] = useState<BoxPerson[]>([]);
  const [catalog, setCatalog] = useState<BoxCatalogMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<BoxMovementFilters>({});

  const [launchOpen, setLaunchOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMovements(await fetchBoxMovements(filters));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao carregar lançamentos");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshPeople = useCallback(() => {
    void fetchBoxPeople().then(setPeople).catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshPeople();
    void fetchBoxCatalog().then(setCatalog).catch(() => undefined);
  }, [refreshPeople]);

  return (
    <div className="container py-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Controle de Caixas</h1>
          <p className="text-sm text-muted-foreground">
            Empréstimos e vendas de caixas, devoluções e situação por pessoa.
          </p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <>
              <Button variant="outline" size="sm" onClick={() => void downloadBoxReport("pdf", filters)}>
                <FileDown className="mr-1 h-4 w-4" /> PDF
              </Button>
              <Button variant="outline" size="sm" onClick={() => void downloadBoxReport("xlsx", filters)}>
                <FileDown className="mr-1 h-4 w-4" /> XLSX
              </Button>
            </>
          )}
          {canLaunch && (
            <Button size="sm" onClick={() => setLaunchOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> Novo lançamento
            </Button>
          )}
        </div>
      </div>

      <FilterBar
        filters={filters}
        people={people}
        catalog={catalog}
        onChange={setFilters}
        onClear={() => setFilters({})}
      />

      <div className="rounded-lg border border-border">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando…
          </div>
        ) : movements.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            Nenhum lançamento encontrado para os filtros atuais.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Pessoa</TableHead>
                <TableHead>Itens</TableHead>
                <TableHead>Operação</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead className="text-right">Pendente</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {movements.map((m) => (
                <TableRow key={m.id} className={m.reversed ? "opacity-60" : undefined}>
                  <TableCell className="whitespace-nowrap">{formatDate(m.operationDate)}</TableCell>
                  <TableCell>{m.personName}</TableCell>
                  <TableCell className="max-w-[320px] text-sm text-muted-foreground">{itemsSummary(m.items)}</TableCell>
                  <TableCell>{OPERATION_LABEL[m.operation]}</TableCell>
                  <TableCell>
                    <Badge variant={m.reversed ? "outline" : "secondary"}>{STATUS_LABEL[m.status]}</Badge>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {m.operation === "EMPRESTIMO"
                      ? `${loanPending(m)} cx`
                      : m.financialVisible
                        ? formatMoney(m.pendingAmount)
                        : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setDetailId(m.id)}>
                      Detalhes
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {launchOpen && (
        <LaunchDialog
          people={people}
          catalog={catalog}
          canCreatePerson={canCreatePerson}
          onPersonCreated={refreshPeople}
          onClose={() => setLaunchOpen(false)}
          onSaved={() => {
            setLaunchOpen(false);
            void load();
          }}
        />
      )}

      {detailId && (
        <DetailDialog
          movementId={detailId}
          canReturn={canReturn}
          canReceive={canReceive}
          canCorrect={canCorrect}
          onClose={() => setDetailId(null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function FilterBar({
  filters, people, catalog, onChange, onClear,
}: {
  filters: BoxMovementFilters;
  people: BoxPerson[];
  catalog: BoxCatalogMaterial[];
  onChange: (f: BoxMovementFilters) => void;
  onClear: () => void;
}) {
  const set = (patch: Partial<BoxMovementFilters>) => onChange({ ...filters, ...patch });
  const selectedMaterial = catalog.find((m) => m.id === filters.materialId);
  // Filtros oferecem TODAS as opções (inclusive inativas, identificadas) para consultas históricas.
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border p-3">
      <div className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
        <Filter className="h-4 w-4" /> Filtros
      </div>
      <div className="space-y-1">
        <Label className="text-xs">De</Label>
        <Input type="date" value={filters.from ?? ""} onChange={(e) => set({ from: e.target.value || undefined })} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Até</Label>
        <Input type="date" value={filters.to ?? ""} onChange={(e) => set({ to: e.target.value || undefined })} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Pessoa</Label>
        <Select value={filters.personId ?? ALL} onValueChange={(v) => set({ personId: v === ALL ? undefined : v })}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Todas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas</SelectItem>
            {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Operação</Label>
        <Select
          value={filters.operation ?? ALL}
          onValueChange={(v) => set({ operation: v === ALL ? undefined : (v as BoxOperation) })}
        >
          <SelectTrigger className="w-36"><SelectValue placeholder="Todas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas</SelectItem>
            <SelectItem value="EMPRESTIMO">Empréstimo</SelectItem>
            <SelectItem value="VENDA">Venda</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Material</Label>
        <Select
          value={filters.materialId ?? ALL}
          onValueChange={(v) => set({ materialId: v === ALL ? undefined : v, typeId: undefined })}
        >
          <SelectTrigger className="w-40"><SelectValue placeholder="Todos" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos</SelectItem>
            {catalog.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.name}{m.active ? "" : " (inativo)"}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {selectedMaterial && selectedMaterial.types.length > 0 && (
        <div className="space-y-1">
          <Label className="text-xs">Tipo</Label>
          <Select
            value={filters.typeId ?? ALL}
            onValueChange={(v) => set({ typeId: v === ALL ? undefined : v })}
          >
            <SelectTrigger className="w-40"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos</SelectItem>
              {selectedMaterial.types.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}{t.active ? "" : " (inativo)"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <Button variant="ghost" size="sm" onClick={onClear}>Limpar filtros</Button>
    </div>
  );
}

type DraftItem = { materialId: string; typeId: string; quantity: string };

function LaunchDialog({
  people, catalog, canCreatePerson, onPersonCreated, onClose, onSaved,
}: {
  people: BoxPerson[];
  catalog: BoxCatalogMaterial[];
  canCreatePerson: boolean;
  onPersonCreated: () => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Novos lançamentos só oferecem opções ATIVAS.
  const activeMaterials = useMemo(() => catalog.filter((m) => m.active), [catalog]);
  const activeTypesOf = useCallback(
    (materialId: string) => (catalog.find((m) => m.id === materialId)?.types ?? []).filter((t) => t.active),
    [catalog],
  );
  const firstDraft = useCallback((): DraftItem => {
    const material = activeMaterials[0];
    if (!material) return { materialId: "", typeId: "", quantity: "" };
    const types = activeTypesOf(material.id);
    return { materialId: material.id, typeId: types[0]?.id ?? "", quantity: "" };
  }, [activeMaterials, activeTypesOf]);

  const [step, setStep] = useState<1 | 2>(1);
  const [operation, setOperation] = useState<BoxOperation>("EMPRESTIMO");
  const [items, setItems] = useState<DraftItem[]>([firstDraft()]);
  const [creatingPerson, setCreatingPerson] = useState(false);
  const [personId, setPersonId] = useState("");
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [operationDate, setOperationDate] = useState(todayInBrasilia());
  const [observation, setObservation] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [requestId] = useState(newRequestId());

  const personLabel = creatingPerson
    ? newName.trim() || "(nova pessoa)"
    : people.find((p) => p.id === personId)?.name ?? "—";

  const addItem = () => setItems((prev) => [...prev, firstDraft()]);
  const removeItem = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const patchItem = (idx: number, patch: Partial<DraftItem>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const labelFor = useCallback(
    (it: DraftItem): string => {
      const material = catalog.find((m) => m.id === it.materialId);
      if (!material) return "—";
      const type = material.types.find((t) => t.id === it.typeId);
      return materialTypeLabel(material.name, type?.name ?? null);
    },
    [catalog],
  );

  const itemLabels = useMemo(
    () => items.map((it) => {
      const qty = Number.parseInt(it.quantity, 10);
      return `${labelFor(it)} ×${Number.isNaN(qty) ? "?" : qty}`;
    }),
    [items, labelFor],
  );

  // Texto assinado (preservado no backend) — rótulos claros, um item por linha.
  const signedSummary = useMemo(
    () =>
      [
        `Operação: ${OPERATION_LABEL[operation]}`,
        `Pessoa: ${personLabel}`,
        `Data: ${formatDate(operationDate)}`,
        "Itens:",
        ...itemLabels.map((l) => `- ${l}`),
      ].join("\n"),
    [operation, personLabel, operationDate, itemLabels],
  );

  const validateStep1 = (): string | null => {
    if (activeMaterials.length === 0) return "Nenhum material ativo cadastrado. Cadastre em Materiais e tipos de caixa.";
    if (items.length === 0) return "Adicione ao menos um material";
    for (const [i, it] of items.entries()) {
      if (!it.materialId) return `Selecione o material (item ${i + 1})`;
      const material = catalog.find((m) => m.id === it.materialId);
      const hasActiveTypes = activeTypesOf(it.materialId).length > 0;
      if (material?.hasTypes) {
        if (!hasActiveTypes) return `O material do item ${i + 1} não tem tipo ativo disponível`;
        if (!it.typeId) return `Selecione o tipo da caixa (item ${i + 1})`;
      }
      const qty = Number.parseInt(it.quantity, 10);
      if (Number.isNaN(qty) || qty <= 0) return `Informe uma quantidade inteira positiva (item ${i + 1})`;
    }
    if (creatingPerson) {
      if (!canCreatePerson) return "Sem permissão para cadastrar pessoa";
      if (!newName.trim()) return "Informe o nome da pessoa";
    } else if (!personId) {
      return "Selecione a pessoa que está retirando";
    }
    return null;
  };

  const goReview = () => {
    const error = validateStep1();
    if (error) { toast.error(error); return; }
    setStep(2);
  };

  const confirm = async () => {
    if (!signature) { toast.error("Capture a assinatura de recebimento"); return; }
    const payloadItems: CreateBoxMovementItemInput[] = items.map((it) => {
      const material = catalog.find((m) => m.id === it.materialId);
      return {
        materialId: it.materialId,
        typeId: material?.hasTypes ? (it.typeId || null) : null,
        quantity: Number.parseInt(it.quantity, 10),
      };
    });
    setSaving(true);
    try {
      await createBoxMovement({
        operation,
        personId: creatingPerson ? undefined : personId,
        newPersonName: creatingPerson ? newName.trim() : undefined,
        newPersonPhone: creatingPerson && newPhone.trim() ? newPhone.trim() : undefined,
        newPersonNotes: creatingPerson && newNotes.trim() ? newNotes.trim() : undefined,
        items: payloadItems,
        operationDate,
        observation: observation.trim() || undefined,
        signatureImage: signature,
        signedSummary,
        clientRequestId: requestId,
      });
      if (creatingPerson) onPersonCreated();
      toast.success("Lançamento registrado");
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao registrar lançamento");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{step === 1 ? "Novo lançamento" : "Confirmar recebimento"}</DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Vários materiais podem sair no mesmo lançamento."
              : "Revise o resumo e colha a assinatura de quem está retirando."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Operação</Label>
              <Select value={operation} onValueChange={(v) => setOperation(v as BoxOperation)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMPRESTIMO">Empréstimo</SelectItem>
                  <SelectItem value="VENDA">Venda</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Materiais</Label>
              {items.map((it, idx) => {
                const material = catalog.find((m) => m.id === it.materialId);
                const types = it.materialId ? activeTypesOf(it.materialId) : [];
                return (
                  <div key={idx} className="flex items-end gap-2 rounded-md border border-border p-2">
                    <div className="flex-1 space-y-1">
                      <span className="text-xs text-muted-foreground">Material</span>
                      <Select
                        value={it.materialId}
                        onValueChange={(v) => {
                          const next = activeTypesOf(v);
                          patchItem(idx, { materialId: v, typeId: next[0]?.id ?? "" });
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="Material" /></SelectTrigger>
                        <SelectContent>
                          {activeMaterials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {material?.hasTypes && (
                      <div className="flex-1 space-y-1">
                        <span className="text-xs text-muted-foreground">Tipo</span>
                        <Select value={it.typeId} onValueChange={(v) => patchItem(idx, { typeId: v })}>
                          <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
                          <SelectContent>
                            {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="w-20 space-y-1">
                      <span className="text-xs text-muted-foreground">Qtd</span>
                      <Input type="number" min={1} step={1} value={it.quantity}
                        onChange={(e) => patchItem(idx, { quantity: e.target.value })} />
                    </div>
                    <Button type="button" variant="ghost" size="icon" className="shrink-0"
                      onClick={() => removeItem(idx)} disabled={items.length === 1} title="Remover material">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="mr-1 h-4 w-4" /> Adicionar material
              </Button>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label>Pessoa que está retirando</Label>
                {canCreatePerson && (
                  <button type="button" className="text-xs text-primary hover:underline"
                    onClick={() => setCreatingPerson((v) => !v)}>
                    {creatingPerson ? "Selecionar existente" : "Cadastrar nova"}
                  </button>
                )}
              </div>
              {creatingPerson ? (
                <div className="space-y-2 rounded-md border border-dashed border-border p-2">
                  <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nome" />
                  <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Telefone (opcional)" />
                  <Input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="Observação (opcional)" />
                </div>
              ) : (
                <Select value={personId} onValueChange={setPersonId}>
                  <SelectTrigger><SelectValue placeholder="Selecione a pessoa" /></SelectTrigger>
                  <SelectContent>
                    {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Data da operação</Label>
                <Input type="date" value={operationDate} onChange={(e) => setOperationDate(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Observação (opcional)</Label>
              <Textarea value={observation} onChange={(e) => setObservation(e.target.value)} rows={2} />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
              <div className="mb-2 font-medium">Resumo da retirada</div>
              <dl className="space-y-1">
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 text-muted-foreground">Operação</dt>
                  <dd className="font-medium">{OPERATION_LABEL[operation]}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 text-muted-foreground">Pessoa</dt>
                  <dd>{personLabel}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 text-muted-foreground">Data</dt>
                  <dd>{formatDate(operationDate)}</dd>
                </div>
              </dl>
              <div className="mt-2">
                <div className="mb-1 text-muted-foreground">Itens</div>
                <ul className="list-disc space-y-0.5 pl-5">
                  {itemLabels.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                Data/hora registradas automaticamente no horário de Brasília ao confirmar.
              </div>
            </div>
            <div className="space-y-1">
              <Label>Assinatura de recebimento</Label>
              <BoxSignaturePad onChange={setSignature} disabled={saving} />
              <p className="text-xs text-muted-foreground">
                A assinatura confirma o recebimento das caixas descritas acima.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="ghost" onClick={onClose}>Cancelar</Button>
              <Button onClick={goReview}>Revisar e assinar</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep(1)} disabled={saving}>Voltar</Button>
              <Button onClick={() => void confirm()} disabled={saving || !signature}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirmar lançamento
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({
  movementId, canReturn, canReceive, canCorrect, onClose, onChanged,
}: {
  movementId: string;
  canReturn: boolean;
  canReceive: boolean;
  canCorrect: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [movement, setMovement] = useState<BoxMovement | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<"view" | "return" | "payment">("view");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setMovement(await fetchBoxMovement(movementId));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao carregar o lançamento");
    } finally {
      setLoading(false);
    }
  }, [movementId]);

  useEffect(() => { void reload(); }, [reload]);

  const afterChange = (updated: BoxMovement) => {
    setMovement(updated);
    setMode("view");
    onChanged();
  };

  const isSale = movement?.operation === "VENDA";
  const financial = movement?.financialVisible ?? false;
  const active = movement && !movement.reversed;
  const hasPendingReturn = (movement?.items ?? []).some((it) => (it.pendingQuantity ?? 0) > 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Lançamento</DialogTitle>
        </DialogHeader>
        {loading || !movement ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando…
          </div>
        ) : mode === "return" ? (
          <ReturnForm movement={movement} onCancel={() => setMode("view")} onDone={afterChange} />
        ) : mode === "payment" ? (
          <PaymentForm movement={movement} onCancel={() => setMode("view")} onDone={afterChange} />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Info label="Pessoa" value={movement.personName} />
              <Info label="Operação" value={OPERATION_LABEL[movement.operation]} />
              <Info label="Data da operação" value={formatDate(movement.operationDate)} />
              <Info label="Data/hora (Brasília)" value={formatDateTimeBrasilia(movement.withdrawnAt)} />
              <Info label="Situação" value={STATUS_LABEL[movement.status]} />
              <Info label="Conferente" value={movement.createdByEmail} />
              {isSale && financial && (
                <>
                  <Info label="Valor total" value={formatMoney(movement.saleTotal)} />
                  <Info label="Recebido" value={formatMoney(movement.receivedAmount)} />
                  <Info label="Pendente" value={formatMoney(movement.pendingAmount)} />
                </>
              )}
            </div>

            <div>
              <div className="mb-1 text-sm font-medium">Itens</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Material/Tipo</TableHead>
                    <TableHead className="text-right">Qtd</TableHead>
                    {movement.operation === "EMPRESTIMO" && <TableHead className="text-right">Devolvidas</TableHead>}
                    {movement.operation === "EMPRESTIMO" && <TableHead className="text-right">Pendentes</TableHead>}
                    {isSale && financial && <TableHead className="text-right">Subtotal</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movement.items.map((it) => (
                    <TableRow key={it.itemId}>
                      <TableCell>{materialTypeLabel(it.materialName, it.typeName)}</TableCell>
                      <TableCell className="text-right">{it.quantity}</TableCell>
                      {movement.operation === "EMPRESTIMO" && (
                        <TableCell className="text-right">{it.returnedQuantity ?? 0}</TableCell>
                      )}
                      {movement.operation === "EMPRESTIMO" && (
                        <TableCell className="text-right">{it.pendingQuantity ?? 0}</TableCell>
                      )}
                      {isSale && financial && (
                        <TableCell className="text-right">{formatMoney(it.saleLineTotal)}</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {movement.observation && (
              <div className="text-sm"><span className="text-muted-foreground">Observação: </span>{movement.observation}</div>
            )}
            {movement.reversed && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm">
                <strong>Estornado.</strong> {movement.reversalReason}
              </div>
            )}

            {movement.signatureImage && (
              <div>
                <div className="mb-1 text-sm font-medium">Assinatura de recebimento</div>
                <img src={movement.signatureImage} alt="Assinatura" className="h-24 rounded border border-border bg-white" />
              </div>
            )}

            {movement.settlements && movement.settlements.length > 0 && (
              <div>
                <div className="mb-1 text-sm font-medium">Baixas</div>
                <div className="space-y-1">
                  {movement.settlements.map((s) => {
                    const item = s.itemId ? movement.items.find((it) => it.itemId === s.itemId) : null;
                    return (
                      <div key={s.id} className={`flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-sm ${s.reversed ? "opacity-50" : ""}`}>
                        <span>
                          {formatDate(s.settledDate)} —{" "}
                          {s.type === "DEVOLUCAO"
                            ? `Devolução de ${s.quantity} cx${item ? ` (${materialTypeLabel(item.materialName, item.typeName)})` : ""}`
                            : `Pagamento de ${formatMoney(s.amount)}${s.paymentMethod ? ` (${PAYMENT_METHOD_LABEL[s.paymentMethod]})` : ""}`}
                          {s.reversed && " — estornada"}
                        </span>
                        {canCorrect && !s.reversed && !movement.reversed && (
                          <ReversalButton
                            label="Estornar baixa"
                            onConfirm={async (reason) => afterChange(await reverseBoxSettlement(s.id, reason))}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => void downloadBoxReceipt(movement.id)}>
                <FileDown className="mr-1 h-4 w-4" /> Comprovante
              </Button>
              {active && movement.operation === "EMPRESTIMO" && canReturn && hasPendingReturn && (
                <Button size="sm" onClick={() => setMode("return")}>Registrar devolução</Button>
              )}
              {active && isSale && financial && canReceive && (movement.pendingAmount ?? 0) > 0 && (
                <Button size="sm" onClick={() => setMode("payment")}>Receber pagamento</Button>
              )}
              {active && canCorrect && (
                <ReversalButton
                  label="Estornar lançamento"
                  variant="destructive"
                  onConfirm={async (reason) => afterChange(await reverseBoxMovement(movement.id, reason))}
                />
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div>{value}</div>
    </div>
  );
}

function ReturnForm({
  movement, onCancel, onDone,
}: { movement: BoxMovement; onCancel: () => void; onDone: (m: BoxMovement) => void }) {
  const pendingItems = movement.items.filter((it) => (it.pendingQuantity ?? 0) > 0);
  const [itemId, setItemId] = useState(pendingItems[0]?.itemId ?? "");
  const [quantity, setQuantity] = useState("");
  const [settledDate, setSettledDate] = useState(todayInBrasilia());
  const [observation, setObservation] = useState("");
  const [saving, setSaving] = useState(false);
  const [requestId] = useState(newRequestId());
  const selected = movement.items.find((it) => it.itemId === itemId);
  const pending = selected?.pendingQuantity ?? 0;

  const submit = async () => {
    if (!itemId) { toast.error("Selecione o material a devolver"); return; }
    const qty = Number.parseInt(quantity, 10);
    if (Number.isNaN(qty) || qty <= 0) { toast.error("Informe a quantidade a devolver"); return; }
    if (qty > pending) { toast.error(`Não é possível devolver acima do pendente (${pending})`); return; }
    setSaving(true);
    try {
      const updated = await registerBoxReturn(movement.id, {
        itemId, quantity: qty, settledDate, observation: observation.trim() || undefined, clientRequestId: requestId,
      });
      toast.success("Devolução registrada");
      onDone(updated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao registrar devolução");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label>Material</Label>
        <Select value={itemId} onValueChange={(v) => { setItemId(v); setQuantity(""); }}>
          <SelectTrigger><SelectValue placeholder="Selecione o material" /></SelectTrigger>
          <SelectContent>
            {pendingItems.map((it) => (
              <SelectItem key={it.itemId} value={it.itemId}>
                {materialTypeLabel(it.materialName, it.typeName)} — {it.pendingQuantity} pendente(s)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label>Quantidade a devolver</Label>
        <Input type="number" min={1} max={pending} step={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label>Data</Label>
        <Input type="date" value={settledDate} onChange={(e) => setSettledDate(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label>Observação (opcional)</Label>
        <Textarea value={observation} onChange={(e) => setObservation(e.target.value)} rows={2} />
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button onClick={() => void submit()} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Registrar devolução
        </Button>
      </DialogFooter>
    </div>
  );
}

function PaymentForm({
  movement, onCancel, onDone,
}: { movement: BoxMovement; onCancel: () => void; onDone: (m: BoxMovement) => void }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<BoxPaymentMethod>("DINHEIRO");
  const [settledDate, setSettledDate] = useState(todayInBrasilia());
  const [observation, setObservation] = useState("");
  const [saving, setSaving] = useState(false);
  const [requestId] = useState(newRequestId());
  const pending = movement.pendingAmount ?? 0;

  const submit = async () => {
    const value = Number.parseFloat(amount.replace(",", "."));
    if (Number.isNaN(value) || value <= 0) { toast.error("Informe o valor recebido"); return; }
    if (value > pending + 0.0001) { toast.error("Não é possível receber acima do saldo pendente"); return; }
    setSaving(true);
    try {
      const updated = await registerBoxPayment(movement.id, {
        amount: value, paymentMethod: method, settledDate,
        observation: observation.trim() || undefined, clientRequestId: requestId,
      });
      toast.success("Pagamento registrado");
      onDone(updated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao registrar pagamento");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Saldo pendente: <strong>{formatMoney(pending)}</strong></p>
      <div className="space-y-1">
        <Label>Valor recebido</Label>
        <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" />
      </div>
      <div className="space-y-1">
        <Label>Forma de pagamento</Label>
        <Select value={method} onValueChange={(v) => setMethod(v as BoxPaymentMethod)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{PAYMENT_METHOD_LABEL[m]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label>Data</Label>
        <Input type="date" value={settledDate} onChange={(e) => setSettledDate(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label>Observação (opcional)</Label>
        <Textarea value={observation} onChange={(e) => setObservation(e.target.value)} rows={2} />
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button onClick={() => void submit()} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Registrar pagamento
        </Button>
      </DialogFooter>
    </div>
  );
}

function ReversalButton({
  label, variant = "outline", onConfirm,
}: {
  label: string;
  variant?: "outline" | "destructive";
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    if (!reason.trim()) { toast.error("Justificativa é obrigatória"); return; }
    setSaving(true);
    try {
      await onConfirm(reason.trim());
      toast.success("Estorno registrado");
      setOpen(false);
      setReason("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao estornar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant={variant} size="sm" onClick={() => setOpen(true)}>
        <RotateCcw className="mr-1 h-4 w-4" /> {label}
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>
              O registro original e o histórico são preservados. Informe a justificativa.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="Motivo do estorno (obrigatório)" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
            <Button variant="destructive" onClick={() => void confirm()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirmar estorno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
